// src/modules/callSync/context/CallSessionContext.jsx
//
// ONE call session for the whole app.
//
// Why a context rather than a hook per button: a phone can only be on one call
// at a time, so "the call in progress" is a property of the *user*, not of the
// row that happened to start it. When each CallButton owned its own hook the
// state died with the component — reloading the browser, closing the drawer or
// switching tabs lost the fact that the phone was still ringing, and every
// other row still offered a Call button that would dial the customer again.
//
// The browser is never the authority. On mount this asks the server what is
// actually in flight (GET /call-sync/calls/active) and resumes from that, so a
// reload, a second tab and the phone itself can never disagree.

import React, {
  createContext, useContext, useState, useRef, useCallback, useEffect, useMemo,
} from 'react';
import {
  startCall as apiStartCall,
  fetchCallStatus,
  fetchActiveCall,
  cancelCall as apiCancelCall,
  fetchPendingResultCalls,
} from '../api';
import { statusMeta, stopAction, MEASURED_SOURCES, CALL_ERROR_HINTS } from '../constants';
import { fetchLeadStatuses } from '../../leads/api';
import { useAuth } from '../../../context/AuthContext';
import CallDispositionModal from '../components/CallDispositionModal';
import GlobalCallBar from '../components/GlobalCallBar';

const POLL_MS = 2000;

// A call does not stop changing when it ends. The phone reads the true answered
// duration out of its call log AFTER the call, and that reconciliation can lag
// by seconds or minutes (a locked, dozing handset is the slow case). So the
// poll keeps going at a gentle rate after the call is terminal until the
// measured figure lands, and the UI upgrades itself from "approx." to measured
// without the user reloading.
const RECONCILE_POLL_MS = 5000;
const RECONCILE_WINDOW_MS = 5 * 60 * 1000;

// Statuses whose duration a call-log reconciliation can still refine. A call
// that never left the device has nothing to reconcile.
const REFINABLE = ['DISCONNECTED', 'NO_ANSWER', 'CONNECTED'];

const CallSessionContext = createContext(null);

export function CallSessionProvider({ children }) {
  // A signed-out browser has no call and no authority to ask about one, so the
  // whole session stays inert until somebody is logged in.
  const { user } = useAuth();
  const enabled = !!user;

  const [call, setCall] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  // True once the server has been asked what is in flight. Until then the Call
  // buttons stay disabled — offering to dial before we know whether a call is
  // already running is exactly the double-dial this module exists to prevent.
  const [hydrated, setHydrated] = useState(false);
  // Bumped whenever a call reaches a terminal state, so lists can refresh
  // without every one of them registering a callback.
  const [lastFinishedAt, setLastFinishedAt] = useState(0);
  // Ticks once a second so the live "Connected — 00:42" timer moves between polls.
  const [, setTick] = useState(0);

  const [finishedCall, setFinishedCall] = useState(null);
  const [dispositionOpen, setDispositionOpen] = useState(false);
  // A finished call still owing an outcome that we found on page load, rather
  // than watched end. Offered, not forced — see hydrate().
  const [outcomeOwed, setOutcomeOwed] = useState(null);
  // Every call of today's that is still owed a result — not just the one the
  // 30-minute window caught. Under a result-saved target rule each of these has
  // earned the rep nothing yet, so the count is worth stating plainly.
  const [awaitingResult, setAwaitingResult] = useState({ pendingCount: 0, lapsedCount: 0, pending: [] });
  const [leadStatuses, setLeadStatuses] = useState([]);

  const pollRef = useRef(null);
  const tickRef = useRef(null);
  const completedRef = useRef(false);
  // Guards against a stale poll for a previous call overwriting a newer one.
  const watchedIdRef = useRef(null);
  // `hydrate` runs from an interval and an event listener, so it reads the
  // current call through refs rather than a closed-over render value.
  const callRef = useRef(null);
  const dispositionOpenRef = useRef(false);
  // Outcome prompts the user has deliberately closed. Without this, hydrating
  // on every window focus would keep re-opening a prompt they just dismissed.
  const dismissedRef = useRef(new Set());

  useEffect(() => { callRef.current = call; }, [call]);
  useEffect(() => { dispositionOpenRef.current = dispositionOpen; }, [dispositionOpen]);

  const stopTimers = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  }, []);

  useEffect(() => stopTimers, [stopTimers]);

  useEffect(() => {
    if (!enabled) return;
    fetchLeadStatuses().then((r) => setLeadStatuses(r?.data?.data || [])).catch(() => {});
  }, [enabled]);

  // Signing out must not leave one user's call ticking away in another's tab.
  useEffect(() => {
    if (enabled) return;
    stopTimers();
    watchedIdRef.current = null;
    completedRef.current = false;
    setCall(null);
    setError(null);
    setFinishedCall(null);
    setDispositionOpen(false);
    setOutcomeOwed(null);
  }, [enabled, stopTimers]);

  const onCompleted = useCallback((finished) => {
    setLastFinishedAt(Date.now());
    // Only ask for an outcome when a call actually happened. Prompting after a
    // failed dispatch would be noise.
    if (REFINABLE.includes(finished.status) && !finished.disposition) {
      // This one we watched end, so opening the prompt is expected rather than
      // an ambush.
      setOutcomeOwed(null);
      setFinishedCall(finished);
      setDispositionOpen(true);
    }
  }, []);

  /** Open the outcome prompt for a call that was found already finished. */
  const openOutcome = useCallback(() => {
    setOutcomeOwed((owed) => {
      if (owed) { setFinishedCall(owed); setDispositionOpen(true); }
      return null;
    });
  }, []);

  // ── Polling ───────────────────────────────────────────────────────────────

  const poll = useCallback(async (callId) => {
    try {
      const res = await fetchCallStatus(callId);
      const next = res.data.data;
      if (watchedIdRef.current !== callId) return; // a newer call took over
      // The status endpoint is deliberately lean; keep the lead we already know
      // about so the UI does not lose the customer's name mid-call.
      setCall((prev) => (prev && prev.callId === callId && prev.lead
        ? { ...prev, ...next, lead: next.lead || prev.lead }
        : next));

      if (!statusMeta(next.status).live) {
        if (!completedRef.current) {
          completedRef.current = true;
          onCompleted(next);
          // Downshift to the slow reconcile watch rather than stopping dead.
          startReconcileWatch(callId, next);
        }
      }
    } catch (err) {
      // A blip must not kill the session; the next tick retries. Only a 404/403
      // is terminal.
      const status = err?.response?.status;
      if (status === 404 || status === 403) {
        stopTimers();
        watchedIdRef.current = null;
        setError({ code: 'NOT_FOUND', message: 'This call is no longer available.' });
      }
    }
    // startReconcileWatch is defined below and stable via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCompleted, stopTimers]);

  // Kept in a ref so `poll` can call it without a circular dependency.
  const startReconcileWatchRef = useRef(() => {});
  const startReconcileWatch = useCallback((callId, finished) => {
    startReconcileWatchRef.current(callId, finished);
  }, []);

  useEffect(() => {
    startReconcileWatchRef.current = (callId, finished) => {
      stopTimers();
      // Already measured, or a call that never reached the network — nothing
      // left for the phone to tell us.
      if (MEASURED_SOURCES.includes(finished.durationSource)
        || !REFINABLE.includes(finished.status)) return;

      const until = Date.now() + RECONCILE_WINDOW_MS;
      pollRef.current = setInterval(async () => {
        if (Date.now() > until || watchedIdRef.current !== callId) { stopTimers(); return; }
        try {
          const res = await fetchCallStatus(callId);
          const next = res.data.data;
          if (watchedIdRef.current !== callId) return;
          setCall((prev) => (prev && prev.callId === callId ? { ...prev, ...next, lead: next.lead || prev.lead } : prev));
          setFinishedCall((prev) => (prev && prev.callId === callId ? { ...prev, ...next } : prev));
          if (MEASURED_SOURCES.includes(next.durationSource)) stopTimers();
        } catch (err) { /* keep watching; a blip is not an answer */ }
      }, RECONCILE_POLL_MS);
    };
  }, [stopTimers]);

  const watch = useCallback((started, { alreadyCompleted = false } = {}) => {
    stopTimers();
    watchedIdRef.current = started.callId;
    completedRef.current = alreadyCompleted;
    setCall(started);

    if (statusMeta(started.status).live) {
      pollRef.current = setInterval(() => poll(started.callId), POLL_MS);
      tickRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    } else {
      startReconcileWatch(started.callId, started);
    }
  }, [poll, startReconcileWatch, stopTimers]);

  // ── Hydration: what does the SERVER say is happening right now? ───────────

  const hydrate = useCallback(async () => {
    if (!enabled) { setHydrated(true); return; }
    try {
      const res = await fetchActiveCall();
      const { active, pendingDisposition } = res.data.data || {};

      if (active) {
        watch(active);
      } else if (watchedIdRef.current && statusMeta(callRef.current?.status || '').live) {
        // We thought a call was live and the server says otherwise — believe
        // the server, and re-read the call so the outcome prompt still appears.
        const callId = watchedIdRef.current;
        try {
          const s = await fetchCallStatus(callId);
          watch(s.data.data, { alreadyCompleted: false });
        } catch (err) { /* it will be picked up as a pending disposition */ }
      }

      // A call that ended while the tab was closed still owes an outcome — but
      // finding one on page load must NOT throw a dialog over the top of a call
      // that is happening right now, and must not ambush the user on every
      // reload for a call they have already moved on from. It is offered in the
      // call bar instead, and only opens when they ask for it.
      if (pendingDisposition && !dispositionOpenRef.current
        && !dismissedRef.current.has(pendingDisposition.callId)) {
        setOutcomeOwed(pendingDisposition);
      } else if (!pendingDisposition) {
        setOutcomeOwed(null);
      }

      // The whole day's unsaved backlog, so the bar can say how much credit is
      // still unclaimed rather than only naming the most recent call.
      try {
        const owed = await fetchPendingResultCalls({ limit: 50 });
        setAwaitingResult(owed.data.data || { pendingCount: 0, lapsedCount: 0, pending: [] });
      } catch (err) { /* the bar simply shows no count */ }
    } catch (err) {
      // Not being able to ask is not the same as "no call in flight"; leave the
      // UI as it is rather than inventing an answer.
    } finally {
      setHydrated(true);
    }
  }, [enabled, watch]);

  useEffect(() => { hydrate(); }, [hydrate]);

  // A tab that was in the background (or a phone whose browser was suspended)
  // catches up the moment it is looked at again.
  useEffect(() => {
    if (!enabled) return undefined;
    const onVisible = () => { if (document.visibilityState === 'visible') hydrate(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [enabled, hydrate]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const start = useCallback(async ({ leadId, phoneNumber, salesRepId, lead } = {}) => {
    setError(null);
    setStarting(true);
    stopTimers();

    // A key unique to this attempt: a retry of the same click is deduplicated
    // server-side, so the phone can never dial twice for one press.
    const idempotencyKey = `${leadId || phoneNumber}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    try {
      const res = await apiStartCall({ leadId, phoneNumber, salesRepId }, idempotencyKey);
      const started = res.data.data;
      // Carry the lead we already have on screen so the live banner can name
      // the customer without a second request.
      watch(lead && !started.lead ? { ...started, lead } : started);
      return started;
    } catch (err) {
      const data = err?.response?.data || {};
      const code = data.code || 'UNKNOWN';
      setError({
        code,
        message: data.message || CALL_ERROR_HINTS[code] || 'Could not start the call.',
        hint: CALL_ERROR_HINTS[code],
      });
      // CALL_IN_PROGRESS means the server knows about a call this browser has
      // lost track of (the classic post-reload case) — go and pick it up.
      if (code === 'CALL_IN_PROGRESS') hydrate();
      return null;
    } finally {
      setStarting(false);
    }
  }, [hydrate, stopTimers, watch]);

  /**
   * Cancel means "do not dial" — it is not a hang-up. The CRM cannot end a call
   * on the rep's handset (the app is not the device's dialer), so once
   * ACTION_CALL has fired the backend refuses with ALREADY_DIALING. That refusal
   * used to be swallowed, which is why the button appeared to do nothing: the
   * call carried on and the UI never said why. Now it is surfaced.
   */
  const cancel = useCallback(async () => {
    const callId = call?.callId;
    if (!callId) return;

    let refused = null;
    try {
      await apiCancelCall(callId);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data || {};
      // 404 = it is already gone, which is the outcome we wanted anyway.
      if (status && status !== 404) {
        refused = {
          code: data.code || 'CANCEL_FAILED',
          message: data.message || 'Could not cancel this call.',
          hint: data.code === 'ALREADY_DIALING'
            ? 'End the call on the handset. To let the CRM hang up, set the Call Sync '
              + 'app as the phone\'s default phone app (app → Settings).'
            : CALL_ERROR_HINTS[data.code],
        };
      }
    }

    // Re-read either way: the server, not this browser, says what happened.
    try {
      const res = await fetchCallStatus(callId);
      const next = res.data.data;
      watch(next, { alreadyCompleted: !statusMeta(next.status).live });
      if (refused && statusMeta(next.status).live) setError(refused);
      else setLastFinishedAt(Date.now());
    } catch (err) {
      stopTimers();
      setCall((c) => (c ? { ...c, status: 'CANCELLED' } : c));
      setLastFinishedAt(Date.now());
    }
  }, [call, stopTimers, watch]);

  const reset = useCallback(() => {
    stopTimers();
    watchedIdRef.current = null;
    completedRef.current = false;
    setCall(null);
    setError(null);
  }, [stopTimers]);

  // ── Derived ───────────────────────────────────────────────────────────────

  const isLive = !!call && statusMeta(call.status).live;

  // Live seconds since the customer answered. Only meaningful once connectedAt
  // exists — before that there is nothing honest to count.
  const liveSeconds = (() => {
    if (!call?.connectedAt) return null;
    const end = call.endedAt ? new Date(call.endedAt) : new Date();
    return Math.max(0, Math.floor((end - new Date(call.connectedAt)) / 1000));
  })();

  const activeLeadId = call && isLive
    ? String(call.lead?._id || call.lead || '') || null
    : null;

  const value = useMemo(() => ({
    call,
    error,
    starting,
    hydrated,
    isLive,
    liveSeconds,
    activeLeadId,
    lastFinishedAt,
    outcomeOwed,
    awaitingResult,
    openOutcome,
    stopAction: stopAction(call),
    start,
    cancel,
    reset,
    refresh: hydrate,
    leadStatuses,
    /** Is THIS lead the one currently being called? */
    isLiveForLead: (leadId) => !!leadId && activeLeadId === String(leadId),
  }), [call, error, starting, hydrated, isLive, liveSeconds, activeLeadId,
    lastFinishedAt, outcomeOwed, awaitingResult, openOutcome, start, cancel, reset,
    hydrate, leadStatuses]);

  return (
    <CallSessionContext.Provider value={value}>
      {children}
      {/* Anchored to the viewport, not to a row: after a reload this is what
          tells the rep the phone is still on the call. */}
      {enabled && (
        <GlobalCallBar
          call={call}
          error={error}
          liveSeconds={liveSeconds}
          stopAction={stopAction(call)}
          outcomeOwed={outcomeOwed}
          awaitingResult={awaitingResult}
          onCancel={cancel}
          onAddOutcome={openOutcome}
          onDismiss={() => {
            if (outcomeOwed?.callId) dismissedRef.current.add(outcomeOwed.callId);
            setOutcomeOwed(null);
            reset();
          }}
        />
      )}
      {/* Rendered here, not inside a row: the outcome prompt must survive the
          drawer closing, the table re-rendering and a page navigation. */}
      <CallDispositionModal
        call={finishedCall}
        open={dispositionOpen}
        leadStatuses={leadStatuses}
        awaitingResult={awaitingResult}
        onClose={() => {
          if (finishedCall?.callId) dismissedRef.current.add(finishedCall.callId);
          setOutcomeOwed(null);
          setDispositionOpen(false);
          setFinishedCall(null);
          reset();
        }}
        onSaved={() => {
          if (finishedCall?.callId) dismissedRef.current.add(finishedCall.callId);
          setLastFinishedAt(Date.now());
          // The call just earned its credit — refresh the outstanding count so
          // the bar does not keep asking for a result already saved.
          fetchPendingResultCalls({ limit: 50 })
            .then((r) => setAwaitingResult(r.data.data || { pendingCount: 0, lapsedCount: 0, pending: [] }))
            .catch(() => {});
        }}
      />
    </CallSessionContext.Provider>
  );
}

/**
 * The single call session. Safe to call anywhere: outside the provider it
 * returns an inert session, so a component is never crashed by where it is
 * mounted.
 */
export function useCallSession() {
  return useContext(CallSessionContext) || {
    call: null,
    error: null,
    starting: false,
    hydrated: true,
    isLive: false,
    liveSeconds: null,
    activeLeadId: null,
    lastFinishedAt: 0,
    outcomeOwed: null,
    awaitingResult: { pendingCount: 0, lapsedCount: 0, pending: [] },
    openOutcome: () => {},
    stopAction: null,
    leadStatuses: [],
    start: async () => null,
    cancel: async () => {},
    reset: () => {},
    refresh: () => {},
    isLiveForLead: () => false,
  };
}

export default CallSessionContext;
